import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAY_METHOD_LABELS, voidReasonSchema, type SupplierAccountDTO, type SupplierPaymentDTO } from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Kpi,
  PageHeader,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { ArrowLeft, FileMinus, HandCoins, ReceiptText, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { dueText, invoiceLabel, PayDialog } from './PayDialog';

/** Cuenta corriente de un proveedor: lo que se debe, las facturas pendientes, los pagos y las notas de crédito. */
export function SupplierAccountPage() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ['payables', 'account', id], queryFn: () => api.get<SupplierAccountDTO>(`/suppliers/${id}/account`) });
  const [dialog, setDialog] = useState<'PAYMENT' | 'CREDIT_NOTE' | null>(null);
  const [voiding, setVoiding] = useState<SupplierPaymentDTO | null>(null);
  const a = q.data;

  return (
    <>
      <Link to="/proveedores/pagos" className="bf-back">
        <ArrowLeft size={16} /> Pagos a proveedores
      </Link>
      <PageHeader
        title={a?.supplierName ?? 'Proveedor'}
        subtitle="Cuenta corriente: compras, pagos y notas de crédito."
        actions={
          a && (
            <>
              <Button icon={<FileMinus />} onClick={() => setDialog('CREDIT_NOTE')} disabled={a.balanceCents <= 0}>
                Nota de crédito
              </Button>
              <Button variant="primary" icon={<HandCoins />} onClick={() => setDialog('PAYMENT')} disabled={a.balanceCents <= 0}>
                Pagar
              </Button>
            </>
          )
        }
      />
      {!a ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          <div className="bf-kpi-grid">
            <Kpi
              label="Se debe"
              tone="amber"
              icon={<Wallet />}
              value={formatMoney(a.balanceCents)}
              delta={a.invoices.length === 1 ? '1 factura pendiente' : `${a.invoices.length} facturas pendientes`}
            />
            <Kpi
              label="Vencido"
              tone="red"
              icon={<ReceiptText />}
              value={formatMoney(a.overdueCents)}
              delta={a.overdueCents ? 'Pagalo primero' : 'Nada vencido'}
            />
          </div>

          <Card>
            <CardHeader title="Facturas pendientes" />
            <CardBody flush>
              {a.invoices.length === 0 ? (
                <EmptyState icon={<ReceiptText />} title="No se le debe nada" />
              ) : (
                <div className="bf-table-wrap">
                  <table className="bf-table">
                    <thead>
                      <tr>
                        <th>Factura</th>
                        <th>Vencimiento</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                        <th style={{ textAlign: 'right' }}>Pagado</th>
                        <th style={{ textAlign: 'right' }}>Debe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.invoices.map((i) => {
                        const d = dueText(i);
                        return (
                          <tr key={i.purchaseId}>
                            <td>
                              <Link to={`/compras/${i.purchaseId}`} className="bf-cell-main">
                                {invoiceLabel(i)}
                              </Link>
                              <div className="bf-cell-sub">
                                {formatDate(i.date)} · {i.branchName}
                              </div>
                            </td>
                            <td className={d.tone === 'danger' ? 'bf-text-danger' : d.tone === 'warning' ? 'bf-text-warning' : 'bf-cell-sub'}>
                              {d.text}
                            </td>
                            <td className="bf-num" style={{ textAlign: 'right' }}>
                              {formatMoney(i.totalCents)}
                            </td>
                            <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                              {formatMoney(i.paidCents)}
                            </td>
                            <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                              {formatMoney(i.pendingCents)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Pagos y notas de crédito" />
            <CardBody flush>
              {a.payments.length === 0 ? (
                <EmptyState icon={<HandCoins />} title="Todavía no hay pagos" />
              ) : (
                <div className="bf-table-wrap">
                  <table className="bf-table">
                    <tbody>
                      {a.payments.map((p) => (
                        <tr key={p.id} style={{ opacity: p.voided ? 0.5 : 1 }}>
                          <td className="bf-cell-sub">{formatDate(p.date)}</td>
                          <td>
                            <span className="bf-cell-main">
                              {p.kind === 'PAYMENT' ? `Pago · ${p.method ? PAY_METHOD_LABELS[p.method] : ''}` : 'Nota de crédito'}
                            </span>{' '}
                            {p.voided && <Badge>Anulado</Badge>}
                            {p.checkDueDate && !p.voided && <Badge tone="info">cobran el {formatDate(p.checkDueDate)}</Badge>}
                            <div className="bf-cell-sub">
                              {[p.reference, p.notes, p.allocations.map((x) => `${x.label}: ${formatMoney(x.amountCents)}`).join(' · ')]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                            <div className="bf-cell-sub">
                              {p.createdByName} · {formatDateTime(p.createdAt)}
                              {p.voided && p.voidReason && ` · Anulado: ${p.voidReason}`}
                            </div>
                          </td>
                          <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                            {formatMoney(p.amountCents)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {!p.voided && (
                              <Button size="sm" variant="ghost" onClick={() => setVoiding(p)}>
                                Anular
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Cuenta corriente" subtitle="Todo lo que pasó con este proveedor, del más nuevo al más viejo." />
            <CardBody flush>
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Movimiento</th>
                      <th style={{ textAlign: 'right' }}>Compra</th>
                      <th style={{ textAlign: 'right' }}>Pago</th>
                      <th style={{ textAlign: 'right' }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.ledger.map((r) => (
                      <tr key={`${r.type}-${r.id}`} style={{ opacity: r.voided ? 0.45 : 1, textDecoration: r.voided ? 'line-through' : undefined }}>
                        <td className="bf-cell-sub">{formatDate(r.date)}</td>
                        <td>{r.description}</td>
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          {r.debitCents ? formatMoney(r.debitCents) : ''}
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          {r.creditCents ? formatMoney(r.creditCents) : ''}
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatMoney(r.balanceCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
          {dialog && <PayDialog account={a} kind={dialog} onClose={() => setDialog(null)} />}
          {voiding && <VoidPaymentDialog payment={voiding} onClose={() => setVoiding(null)} />}
        </>
      )}
    </>
  );
}

function VoidPaymentDialog({ payment, onClose }: { payment: SupplierPaymentDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    const parsed = voidReasonSchema.safeParse({ reason });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
    setBusy(true);
    try {
      await api.post(`/supplier-payments/${payment.id}/void`, parsed.data);
      await Promise.all(['payables', 'suppliers', 'purchases', 'cash', 'notifications'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success('Pago anulado', payment.method === 'CASH' ? 'La plata volvió a la caja.' : undefined);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Anular ${payment.kind === 'PAYMENT' ? 'pago' : 'nota de crédito'} de ${formatMoney(payment.amountCents)}`}
      description="Las facturas vuelven a quedar con ese saldo. Queda registrado quién lo anuló y por qué."
    >
      <div className="bf-form">
        {payment.method === 'CASH' && <Callout tone="info">Fue en efectivo: la plata vuelve a la caja abierta.</Callout>}
        <Field label="Motivo" error={error ?? undefined}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void submit()}>
            Anular
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
